import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { BusinessDetails } from '../../addBusinessModal/types';
// Assuming validatePhone is a utility that returns boolean (true if valid)
// For Zod, we'd typically use .refine or a regex.
// For now, we'll keep the existing validatePhone logic if it's complex,
// or replace with a Zod refinement.
// import { validatePhone, validateEmail as utilValidateEmail } from '@/components/portfolio/settings/utils/validationUtils'; // Will use centralized rules
import { 
  businessNameSchema, // Using genericNameFieldSchema("Business name", 2, 100)
  phoneFieldSchema,
  optionalEmailFieldSchema,
  optionalWebsiteFieldSchema,
  requiredStringSchema,
  emailFieldSchema // This is for required emails
} from '@/utils/validationRules';


// Define Zod schemas for each section based on BusinessDetails from addBusinessModal/types
// and userEmail/termsAccepted for the user details section.

const basicInfoSchema = z.object({
  name: businessNameSchema, // Centralized schema for business name
  phone: phoneFieldSchema,  // Centralized schema for phone
  email: optionalEmailFieldSchema, // Centralized schema for optional email
  website: optionalWebsiteFieldSchema, // Centralized schema for optional website
  // foundingDate is optional and handled by date picker, not typically part of this strict validation block
});

const addressSchema = z.object({
  address: requiredStringSchema("Street address"),
  city: requiredStringSchema("City"),
  state: requiredStringSchema("State"),
  zip: requiredStringSchema("Zip code"), // Could add .length(5) or regex if needed
  timeZone: requiredStringSchema("Timezone"),
});

const industrySchema = z.object({
  industry: requiredStringSchema("Industry"),
});

const userDetailsSchema = z.object({
  userEmail: emailFieldSchema, // Centralized schema for required email
  termsAccepted: z.boolean().refine(val => val === true, { message: "You must accept the terms and conditions" }),
});

type ErrorSetter = React.Dispatch<React.SetStateAction<{ [key: string]: string | undefined }>>;

export const useBusinessFormValidation = (
  businessDetails: BusinessDetails,
  userEmail: string,
  termsAccepted: boolean,
  setErrors: ErrorSetter // Added setErrors to update context
) => {
  const validationResults = useMemo(() => {
    const basicInfoData = {
      name: businessDetails.name,
      phone: businessDetails.phone,
      email: businessDetails.email,
      website: businessDetails.website,
    };
    const addressData = {
      address: businessDetails.address,
      city: businessDetails.city,
      state: businessDetails.state,
      zip: businessDetails.zip,
      timeZone: businessDetails.timeZone,
    };
    const industryData = {
      industry: businessDetails.industry,
    };
    const userDetailsData = {
      userEmail,
      termsAccepted,
    };

    const basicInfoResult = basicInfoSchema.safeParse(basicInfoData);
    const addressResult = addressSchema.safeParse(addressData);
    const industryResult = industrySchema.safeParse(industryData);
    const userDetailsResult = userDetailsSchema.safeParse(userDetailsData);
    
    const currentErrors: { [key: string]: string | undefined } = {};
    
    if (!basicInfoResult.success) {
      for (const issue of basicInfoResult.error.issues) {
        currentErrors[issue.path.join('.')] = issue.message;
      }
    }
    if (!addressResult.success) {
      for (const issue of addressResult.error.issues) {
        currentErrors[issue.path.join('.')] = issue.message;
      }
    }
    if (!industryResult.success) {
      for (const issue of industryResult.error.issues) {
        currentErrors[issue.path.join('.')] = issue.message;
      }
    }
    if (!userDetailsResult.success) {
      for (const issue of userDetailsResult.error.issues) {
        // Adjust path for userEmail and termsAccepted as they are not nested in businessDetails
        if (issue.path.includes('userEmail')) currentErrors['userEmail'] = issue.message;
        else if (issue.path.includes('termsAccepted')) currentErrors['termsAccepted'] = issue.message;
        else currentErrors[issue.path.join('.')] = issue.message;
      }
    }
    
    return {
      isBasicInfoComplete: basicInfoResult.success,
      isAddressComplete: addressResult.success,
      isIndustryComplete: industryResult.success,
      isUserDetailsComplete: userDetailsResult.success,
      currentErrors,
    };
  }, [businessDetails, userEmail, termsAccepted]);

  useEffect(() => {
    setErrors(validationResults.currentErrors);
  }, [validationResults, setErrors]);

  const isSubmitEnabled = 
    validationResults.isBasicInfoComplete &&
    validationResults.isAddressComplete &&
    validationResults.isIndustryComplete &&
    validationResults.isUserDetailsComplete;

  return {
    basicInfoComplete: validationResults.isBasicInfoComplete,
    addressComplete: validationResults.isAddressComplete,
    industryComplete: validationResults.isIndustryComplete,
    userDetailsComplete: validationResults.isUserDetailsComplete,
    isSubmitEnabled,
    // Errors are now set directly into context via setErrors
  };
};
